import { useState, useEffect, useCallback } from 'react';
import { useServices } from '@/app/providers';
import type { LogbookEntry, LogbookParams, CreateLogbookEntryDTO } from '@yawi/types';
import type { PaginatedResponse } from '@yawi/domain';

export function useLogbook(initialParams?: LogbookParams) {
  const { logbookRepo } = useServices();
  const [data, setData] = useState<PaginatedResponse<LogbookEntry> | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [params, setParams] = useState<LogbookParams>(initialParams || { page: 1, limit: 20 });

  const fetchEntries = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await logbookRepo.getEntries(params);
      setData(result);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al cargar la bitácora');
    } finally {
      setLoading(false);
    }
  }, [logbookRepo, params]);

  useEffect(() => {
    fetchEntries();
  }, [fetchEntries]);

  const search = useCallback((searchTerm: string) => {
    setParams(prev => ({ ...prev, search: searchTerm, page: 1 }));
  }, []);

  const filterByType = useCallback((type: LogbookParams['type']) => {
    setParams(prev => ({ ...prev, type, page: 1 }));
  }, []);

  const filterByPriority = useCallback((priority: LogbookParams['priority']) => {
    setParams(prev => ({ ...prev, priority, page: 1 }));
  }, []);

  const filterByDateRange = useCallback((fromDate: string, toDate: string) => {
    setParams(prev => ({ ...prev, fromDate, toDate, page: 1 }));
  }, []);

  const goToPage = useCallback((page: number) => {
    setParams(prev => ({ ...prev, page }));
  }, []);

  const createEntry = useCallback(async (data: CreateLogbookEntryDTO) => {
    try {
      const newEntry = await logbookRepo.createEntry(data);
      // Refresh the list after creating
      fetchEntries();
      return newEntry;
    } catch (err) {
      throw err instanceof Error ? err : new Error('Error al crear entrada');
    }
  }, [logbookRepo, fetchEntries]);

  return {
    entries: data?.data || [],
    pagination: data?.meta,
    loading,
    error,
    params,
    search,
    filterByType,
    filterByPriority,
    filterByDateRange,
    goToPage,
    createEntry,
    refresh: fetchEntries,
  };
}
